import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Badge } from '../components/ui/badge';
import {
  Milestone, Tag, HardHat, TrendingUp,
  CheckCircle2, Landmark, ChevronDown, ChevronUp,
  ExternalLink, RefreshCw, DollarSign,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STAGE_CONFIG = {
  sold_exited: {
    icon: Tag,
    color: 'text-slate-500 dark:text-slate-400',
    bg: 'bg-slate-100 dark:bg-slate-700/40',
    dot: 'bg-slate-400',
    badge: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300',
  },
  listed_for_sale: {
    icon: Tag,
    color: 'text-amber-600 dark:text-amber-400',
    bg: 'bg-amber-50 dark:bg-amber-900/20',
    dot: 'bg-amber-500',
    badge: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  },
  in_renovation: {
    icon: HardHat,
    color: 'text-orange-600 dark:text-orange-400',
    bg: 'bg-orange-50 dark:bg-orange-900/20',
    dot: 'bg-orange-500',
    badge: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
  },
  stabilizing: {
    icon: TrendingUp,
    color: 'text-blue-600 dark:text-blue-400',
    bg: 'bg-blue-50 dark:bg-blue-900/20',
    dot: 'bg-blue-500',
    badge: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  },
  stabilized_operating: {
    icon: CheckCircle2,
    color: 'text-emerald-600 dark:text-emerald-400',
    bg: 'bg-emerald-50 dark:bg-emerald-900/20',
    dot: 'bg-emerald-500',
    badge: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  },
  recently_refinanced: {
    icon: Landmark,
    color: 'text-violet-600 dark:text-violet-400',
    bg: 'bg-violet-50 dark:bg-violet-900/20',
    dot: 'bg-violet-500',
    badge: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300',
  },
};

const CANDIDATE_CONFIG = {
  refinance_candidate: {
    icon: RefreshCw,
    color: 'text-cyan-600 dark:text-cyan-400',
    bg: 'bg-cyan-50 dark:bg-cyan-900/20',
    dot: 'bg-cyan-500',
    badge: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300',
  },
  sale_candidate: {
    icon: DollarSign,
    color: 'text-pink-600 dark:text-pink-400',
    bg: 'bg-pink-50 dark:bg-pink-900/20',
    dot: 'bg-pink-500',
    badge: 'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-300',
  },
};

export default function PropertyLifecycleDashboard() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [stages, setStages] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedStage, setExpandedStage] = useState(null);
  const [expandedCandidate, setExpandedCandidate] = useState(null);

  useEffect(() => {
    fetchLifecycle();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchLifecycle = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/portfolio/lifecycle-summary`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStages(res.data.stages || []);
      setCandidates(res.data.candidates || []);
    } catch {
      // silent fail
    } finally {
      setLoading(false);
    }
  };

  const totalProperties = stages.reduce((acc, s) => acc + s.count, 0);
  const activeStages = stages.filter(s => s.count > 0);
  const activeCandidates = candidates.filter(c => c.count > 0);

  if (loading) {
    return (
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5" data-testid="lifecycle-section">
        <div className="flex items-center gap-2.5 mb-3">
          <div className="w-8 h-8 bg-slate-600 rounded-lg flex items-center justify-center">
            <Milestone className="w-4 h-4 text-white" />
          </div>
          <h3 className="text-sm font-semibold text-slate-800 dark:text-white uppercase tracking-wide">Property Lifecycle</h3>
        </div>
        <div className="animate-pulse space-y-2">
          <div className="h-8 bg-slate-100 dark:bg-slate-700 rounded w-full" />
          <div className="h-8 bg-slate-100 dark:bg-slate-700 rounded w-3/4" />
        </div>
      </div>
    );
  }

  if (totalProperties === 0) {
    return (
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5" data-testid="lifecycle-section">
        <div className="flex items-center gap-2.5 mb-3">
          <div className="w-8 h-8 bg-slate-600 rounded-lg flex items-center justify-center">
            <Milestone className="w-4 h-4 text-white" />
          </div>
          <h3 className="text-sm font-semibold text-slate-800 dark:text-white uppercase tracking-wide">Property Lifecycle</h3>
        </div>
        <p className="text-sm text-slate-400">No properties in portfolio.</p>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden" data-testid="lifecycle-section">
      {/* Header */}
      <div className="px-4 sm:px-5 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 bg-slate-700 dark:bg-slate-600 rounded-lg flex items-center justify-center">
            <Milestone className="w-4 h-4 text-white" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-slate-800 dark:text-white uppercase tracking-wide">
              Property Lifecycle
            </h3>
            <p className="text-[10px] text-slate-400 dark:text-slate-500">
              {totalProperties} properties across {activeStages.length} stages
            </p>
          </div>
        </div>
        {/* Stage summary badges */}
        <div className="flex items-center gap-1.5 flex-wrap justify-end">
          {activeStages.map(stage => {
            const cfg = STAGE_CONFIG[stage.stage_key] || STAGE_CONFIG.stabilizing;
            return (
              <Badge key={stage.stage_key} className={`${cfg.badge} text-[10px] px-1.5 py-0 h-5`}>
                {stage.count} {stage.label}
              </Badge>
            );
          })}
          {activeCandidates.map(cand => {
            const cfg = CANDIDATE_CONFIG[cand.candidate_key] || CANDIDATE_CONFIG.refinance_candidate;
            return (
              <Badge key={cand.candidate_key} className={`${cfg.badge} text-[10px] px-1.5 py-0 h-5 border border-dashed`}>
                {cand.count} {cand.label}
              </Badge>
            );
          })}
        </div>
      </div>

      {/* Stage rows */}
      <div className="border-t border-slate-100 dark:border-slate-700">
        {stages.map(stage => {
          const cfg = STAGE_CONFIG[stage.stage_key] || STAGE_CONFIG.stabilizing;
          const Icon = cfg.icon;
          const isExpanded = expandedStage === stage.stage_key;
          const hasProperties = stage.count > 0;

          return (
            <div key={stage.stage_key} data-testid={`lifecycle-stage-${stage.stage_key}`}>
              {/* Stage row */}
              <div
                className={`flex items-center justify-between px-4 sm:px-5 py-2.5 transition-colors ${
                  hasProperties ? 'cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/30' : 'opacity-40'
                } ${isExpanded ? 'bg-slate-50 dark:bg-slate-700/20' : ''}`}
                onClick={() => hasProperties && setExpandedStage(isExpanded ? null : stage.stage_key)}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-7 h-7 rounded-md flex items-center justify-center ${cfg.bg}`}>
                    <Icon className={`w-3.5 h-3.5 ${cfg.color}`} />
                  </div>
                  <span className="text-sm font-medium text-slate-700 dark:text-slate-200">
                    {stage.label}
                  </span>
                </div>
                <div className="flex items-center gap-2.5">
                  <span className={`text-lg font-bold ${hasProperties ? 'text-slate-900 dark:text-white' : 'text-slate-300 dark:text-slate-600'}`}>
                    {stage.count}
                  </span>
                  {hasProperties && (
                    isExpanded
                      ? <ChevronUp className="w-4 h-4 text-slate-400" />
                      : <ChevronDown className="w-4 h-4 text-slate-400" />
                  )}
                </div>
              </div>

              {/* Expanded property list */}
              {isExpanded && hasProperties && (
                <div className="px-4 sm:px-5 pb-3 pt-1 space-y-1.5">
                  {stage.properties.map(prop => (
                    <div
                      key={prop.id}
                      className="flex items-center justify-between bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-lg px-3 py-2 group hover:border-slate-300 dark:hover:border-slate-600 transition-colors cursor-pointer"
                      onClick={() => navigate(`/properties/${prop.id}`)}
                      data-testid={`lifecycle-property-${prop.id}`}
                    >
                      <div className="flex items-center gap-2.5 min-w-0">
                        <div className={`w-2 h-2 rounded-full flex-shrink-0 ${cfg.dot}`} />
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-slate-800 dark:text-white truncate">
                              {prop.name}
                            </span>
                            {prop.occupancy_pct != null && (
                              <span className="text-[10px] text-slate-400 flex-shrink-0">
                                {prop.occupancy_pct}% occupied
                              </span>
                            )}
                          </div>
                          <p className="text-[11px] text-slate-400 dark:text-slate-500 truncate">
                            {(prop.reasons || []).join(' · ')}
                          </p>
                        </div>
                      </div>
                      <ExternalLink className="w-3.5 h-3.5 text-slate-300 dark:text-slate-600 group-hover:text-slate-500 dark:group-hover:text-slate-400 flex-shrink-0 ml-2 transition-colors" />
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Candidate Signals — additive tags */}
      {activeCandidates.length > 0 && (
        <div className="border-t-2 border-dashed border-slate-200 dark:border-slate-600">
          <div className="px-4 sm:px-5 py-2">
            <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">Candidate Signals</p>
          </div>
          {candidates.map(cand => {
            const cfg = CANDIDATE_CONFIG[cand.candidate_key] || CANDIDATE_CONFIG.refinance_candidate;
            const Icon = cfg.icon;
            const isExpanded = expandedCandidate === cand.candidate_key;
            const hasProperties = cand.count > 0;

            return (
              <div key={cand.candidate_key} data-testid={`lifecycle-candidate-${cand.candidate_key}`}>
                <div
                  className={`flex items-center justify-between px-4 sm:px-5 py-2.5 transition-colors ${
                    hasProperties ? 'cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/30' : 'opacity-40'
                  } ${isExpanded ? 'bg-slate-50 dark:bg-slate-700/20' : ''}`}
                  onClick={() => hasProperties && setExpandedCandidate(isExpanded ? null : cand.candidate_key)}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-7 h-7 rounded-md flex items-center justify-center ${cfg.bg} border border-dashed ${cand.candidate_key === 'refinance_candidate' ? 'border-cyan-300 dark:border-cyan-700' : 'border-pink-300 dark:border-pink-700'}`}>
                      <Icon className={`w-3.5 h-3.5 ${cfg.color}`} />
                    </div>
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-200">
                      {cand.label}
                    </span>
                  </div>
                  <div className="flex items-center gap-2.5">
                    <span className={`text-lg font-bold ${hasProperties ? 'text-slate-900 dark:text-white' : 'text-slate-300 dark:text-slate-600'}`}>
                      {cand.count}
                    </span>
                    {hasProperties && (
                      isExpanded
                        ? <ChevronUp className="w-4 h-4 text-slate-400" />
                        : <ChevronDown className="w-4 h-4 text-slate-400" />
                    )}
                  </div>
                </div>

                {isExpanded && hasProperties && (
                  <div className="px-4 sm:px-5 pb-3 pt-1 space-y-1.5">
                    {cand.properties.map(prop => (
                      <div
                        key={prop.id}
                        className="flex items-center justify-between bg-white dark:bg-slate-800 border border-dashed border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 group hover:border-slate-300 dark:hover:border-slate-600 transition-colors cursor-pointer"
                        onClick={() => navigate(`/properties/${prop.id}`)}
                        data-testid={`candidate-property-${prop.id}`}
                      >
                        <div className="flex items-center gap-2.5 min-w-0">
                          <div className={`w-2 h-2 rounded-full flex-shrink-0 ${cfg.dot}`} />
                          <div className="min-w-0">
                            <span className="text-sm font-medium text-slate-800 dark:text-white truncate block">
                              {prop.name}
                            </span>
                            <p className="text-[11px] text-slate-400 dark:text-slate-500 truncate">
                              {(prop.reasons || []).join(' · ')}
                            </p>
                          </div>
                        </div>
                        <ExternalLink className="w-3.5 h-3.5 text-slate-300 dark:text-slate-600 group-hover:text-slate-500 dark:group-hover:text-slate-400 flex-shrink-0 ml-2 transition-colors" />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
